// @ts-check

/**
 * @file Provides the shared page header component.
 */
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"

import useThemeColors, {useThemeName} from "../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} PageHeaderProps
 * @property {ReactNode} [actions] Optional action controls.
 * @property {string} [subtitle] Optional subtitle text.
 * @property {string} testID Stable test id.
 * @property {string} title Header title.
 */

/** @typedef {Record<string, never>} PageHeaderState */

/**
 * Reusable subtitle + title + actions header used by every Expo screen.
 * @augments {ShapeComponent<PageHeaderProps, PageHeaderState>}
 */
class PageHeader extends ShapeComponent {
  static propTypes = propTypesExact({
    // `PropTypes.node` was producing false positives here (React Native
    // Web wrapped View elements were being flagged "expected a
    // ReactNode" even though `React.isValidElement` returns true for
    // them). The render path just splats `{actions}` into JSX which
    // accepts any renderable, so let prop-types-exact still forbid
    // unknown extras while the value itself is treated as opaque.
    actions: PropTypes.any,
    subtitle: PropTypes.string,
    testID: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })
  static defaultProps = {
    actions: null,
    subtitle: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the page header.
   * @returns {ReactNode} Header content.
   */
  render() {
    const {actions, subtitle, testID, title} = this.p
    return (
      <View
        style={styles.header ||= {
          gap: 12,
          marginBottom: 24
        }}
        testID={testID}
      >
        <View
          style={styles.headerTopRow ||= {
            alignItems: "flex-start",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12,
            justifyContent: "space-between"
          }}
          testID={`${testID}/topRow`}
        >
          <View
            style={styles.headerText ||= {
              gap: 6
            }}
            testID={`${testID}/text`}
          >
            {subtitle &&
              <Text
                style={styles[`subtitle-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontWeight: 600
                }}
                testID={`${testID}/subtitle`}
              >
                {subtitle}
              </Text>
            }
            <Text
              style={styles[`title-${this.tt.themeName}`] ||= {
                fontSize: 28,
                fontWeight: 700
              }}
              testID={`${testID}/title`}
            >
              {title}
            </Text>
          </View>
          {actions &&
            <View
              style={styles.headerActions ||= {
                alignItems: "center",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 10
              }}
              testID={`${testID}/actions`}
            >
              {actions}
            </View>
          }
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<PageHeaderProps>} */
const PageHeaderComponent = memo(shapeComponent(PageHeader))

export default PageHeaderComponent
